import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { projectsApi } from '../api/projects';
import { storiesApi } from '../api/stories';
import { seriesBookBoardApi } from '../api/seriesBookBoard';
import { openStartedBook } from '../utils/openStartedBook';
import {
  PROPOSITION_WORD_CAP,
  START_NOTE,
  boardPayload,
  clampHow,
  clampProposition,
  emptyBoard,
  FREE_CARD_WIDTH,
  GAP_DIRECTIONS,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
  clampAttach,
  clampFreeCardTop,
  clampZoom,
  freeItems,
  insertItem,
  isGondola,
  itemsFromBoard,
  lineInsertIndex,
  lineItems,
  markCardStarted,
  mergeTimelineItems,
  newEntranceCard,
  newExitCard,
  newGapCard,
  newIdeaCard,
  newRevealCard,
  placeFreeCard,
  reorderLineItems,
  seedBooksFromStories,
  shortLine,
  wordCount,
} from '../utils/seriesBookBoard';
import { projectNotesPath } from '../utils/seriesNotes';
import {
  BOOK_LINE_EMPTY,
  BOOK_LINE_LABEL,
  BOOK_LINE_PLACEHOLDER,
} from '../utils/seriesSharedBooks';
import SeriesGetIdeasPanel from '../components/Series/SeriesGetIdeasPanel';
import { SERIES_PLANNING_TOOLS } from '../utils/seriesBeta';
import { GET_IDEAS_LABEL } from '../utils/seriesGetIdeas';
import {
  ADD_A_GAP,
  ADD_IDEA_NOTE,
  ADD_TIMELINE_ITEM_ERROR,
  REVEAL_TITLE,
  REVEAL_UNTITLED,
  REVEAL_WHAT_LABEL,
  REVEAL_WHAT_PLACEHOLDER,
  REVEAL_WHEN_LABEL,
  REVEAL_WHEN_PLACEHOLDER,
  SAVE_TIMELINE_ITEM_ERROR,
  TIMELINE_EMPTY,
} from '../utils/writerCopy';
import './SeriesBookBoardPage.css';

function isFieldTarget(target) {
  return Boolean(target?.closest?.(
    'input, textarea, select, option, a, .sbb-card__action, .sbb-card__short',
  ));
}

function cardClassName(card, extra) {
  const bits = ['sbb-card'];
  if (card.type === 'gap') bits.push('sbb-card--gap');
  if (card.type === 'book') bits.push('sbb-card--book', 'sbb-card--gondola');
  if (card.type === 'entrance') bits.push('sbb-card--entrance');
  if (card.type === 'exit') bits.push('sbb-card--exit');
  if (card.type === 'reveal') bits.push('sbb-card--reveal');
  if (extra) bits.push(extra);
  return bits.join(' ');
}

function hangFor(card) {
  if (card.type === 'gap') return 'on-line';
  if (isGondola(card)) return 'below';
  return 'above';
}

function pinFace(card) {
  if (card.type === 'reveal') {
    const what = (card.what || '').trim();
    const when = shortLine(card.how);
    return {
      title: REVEAL_TITLE,
      status: REVEAL_TITLE,
      whoLabel: REVEAL_WHAT_LABEL,
      whoField: 'what',
      whoValue: card.what || '',
      whoPlaceholder: REVEAL_WHAT_PLACEHOLDER,
      howLabel: REVEAL_WHEN_LABEL,
      howPlaceholder: REVEAL_WHEN_PLACEHOLDER,
      // An empty reveal used to rest as two field labels that read like a
      // filled card; say plainly it is empty and clickable.
      restWho: what || (when ? REVEAL_WHAT_LABEL : REVEAL_UNTITLED),
      restHow: when,
    };
  }
  const entering = card.type === 'entrance';
  const who = (card.character_name || '').trim();
  return {
    title: entering ? 'Entrance' : 'Exit',
    status: entering ? 'Enters' : 'Leaves',
    whoLabel: 'Who',
    whoField: 'character_name',
    whoValue: card.character_name || '',
    whoPlaceholder: 'Name',
    howLabel: entering ? 'How they enter' : 'How they leave',
    howPlaceholder: entering ? 'Walks in, is named, arrives…' : 'Dies, leaves town, vanishes…',
    restWho: who || 'Who',
    restHow: shortLine(card.how) || (entering ? 'How they enter' : 'How they leave'),
  };
}

function gripLabel(card) {
  if (card.type === 'gap') return 'Reorder gap';
  if (card.type === 'reveal') {
    return card.what ? `Move reveal ${card.what}` : 'Move reveal';
  }
  if (card.type === 'entrance') {
    return card.character_name ? `Move entrance ${card.character_name}` : 'Move entrance';
  }
  if (card.type === 'exit') {
    return card.character_name ? `Move exit ${card.character_name}` : 'Move exit';
  }
  return `Reorder ${card.title || 'book'}`;
}

function attachLabel(card) {
  if (card.type === 'reveal') {
    return card.what
      ? `Slide attach point for reveal ${card.what}`
      : 'Slide attach point for reveal';
  }
  if (card.type === 'entrance') {
    return card.character_name
      ? `Slide attach point for entrance ${card.character_name}`
      : 'Slide attach point for entrance';
  }
  if (card.type === 'exit') {
    return card.character_name
      ? `Slide attach point for exit ${card.character_name}`
      : 'Slide attach point for exit';
  }
  return 'Slide attach point';
}

function SortableCard({ card, className, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });
  return (
    <article
      ref={setNodeRef}
      className={`${cardClassName(card, className)}${isDragging ? ' is-dragging' : ''}`}
      data-lane="rail"
      data-hang={hangFor(card)}
      data-testid={card.type === 'book' ? `gondola-card-${card.id}` : `line-card-${card.id}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <button
        type="button"
        className="sbb-card__grip"
        aria-label={gripLabel(card)}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>
      <div className="sbb-card__body">{children}</div>
    </article>
  );
}

// A press that moves less than this is a click (open the pin), not a drag.
const PIN_TAP_SLOP = 4;

function FreeCard({ card, style, onMove, onMoveEnd, onOpen, maxY, onHeight, children }) {
  const cardRef = useRef(null);

  useLayoutEffect(() => {
    const node = cardRef.current;
    if (!node || !onHeight) return undefined;
    const report = () => onHeight(card.id, node.offsetHeight);
    report();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(report);
    observer.observe(node);
    return () => observer.disconnect();
  }, [card.id, onHeight]);

  const onMouseDown = (event) => {
    if (event.button !== 0) return;
    if (isFieldTarget(event.target)) return;
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const origX = Number.isFinite(card.cork_x) ? card.cork_x : 0;
    const storedY = Number.isFinite(card.cork_y) ? card.cork_y : 0;
    const origY = Number.isFinite(maxY) ? Math.min(storedY, maxY) : storedY;
    let moved = false;
    const onMouseMove = (moveEvent) => {
      if (Math.abs(moveEvent.clientX - startX) > PIN_TAP_SLOP
        || Math.abs(moveEvent.clientY - startY) > PIN_TAP_SLOP) {
        moved = true;
      }
      if (!moved) return;
      const nextY = origY + (moveEvent.clientY - startY);
      const ceiling = Number.isFinite(maxY) ? maxY : nextY;
      onMove(card.id, {
        cork_x: Math.max(0, origX + (moveEvent.clientX - startX)),
        cork_y: Math.max(0, Math.min(nextY, ceiling)),
      });
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      // Clicking anywhere on a resting pin opens it. Only the small text
      // button used to, so a click on the title did nothing (run 11).
      if (!moved) {
        if (onOpen) onOpen();
        return;
      }
      onMoveEnd();
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  return (
    <article
      ref={cardRef}
      className={cardClassName(card, 'sbb-card--free')}
      data-lane="free"
      data-hang="above"
      data-testid={`free-card-${card.id}`}
      style={{
        ...style,
        width: FREE_CARD_WIDTH,
        maxWidth: FREE_CARD_WIDTH,
        minWidth: FREE_CARD_WIDTH,
        boxSizing: 'border-box',
      }}
      onMouseDown={onMouseDown}
    >
      <button type="button" className="sbb-card__grip" aria-label={gripLabel(card)}>
        <GripVertical size={16} />
      </button>
      <div className="sbb-card__body">{children}</div>
    </article>
  );
}

function AttachHandle({ card, left, top, lineWidth, getOriginX, onMove, onMoveEnd }) {
  const onMouseDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const onMouseMove = (moveEvent) => {
      const width = Math.max(lineWidth, 1);
      onMove(card.id, { attach: clampAttach((moveEvent.clientX - getOriginX()) / width) });
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      onMoveEnd();
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  return (
    <button
      type="button"
      className="sbb-timeline__attach"
      data-testid={`timeline-attach-${card.id}`}
      aria-label={attachLabel(card)}
      style={{ left, top }}
      onMouseDown={onMouseDown}
    />
  );
}

export default function SeriesBookBoardPage() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const rawView = searchParams.get('view');
  const navigate = useNavigate();
  const {
    currentProject,
    setCurrentProject,
    setCurrentStory,
    setProjectAndStory,
    stories,
    refreshStories,
    createStory,
  } = useApp();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [startingId, setStartingId] = useState(null);
  const [justStartedId, setJustStartedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingPinId, setEditingPinId] = useState(null);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const itemsRef = useRef([]);
  const persistGen = useRef(0);
  const persistChain = useRef(Promise.resolve());
  const trackRef = useRef(null);
  const railRef = useRef(null);
  const [boardSize, setBoardSize] = useState({ width: 800, height: 520, lineY: 248 });
  const [pinHeights, setPinHeights] = useState({});

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const persist = useCallback((nextItems) => {
    if (!projectId) return Promise.resolve();
    const gen = persistGen.current + 1;
    persistGen.current = gen;
    setSaving(true);
    // One PUT at a time. A Book 1 blur must not land after a later
    // Entrance save and wipe Book 3 / Entrance on reload. Merge the
    // snapshot with itemsRef so in-progress sibling fields go out too.
    const run = async () => {
      if (gen !== persistGen.current) return;
      const toSave = mergeTimelineItems(nextItems, itemsRef.current);
      const saved = await seriesBookBoardApi.put(projectId, boardPayload(toSave));
      if (gen !== persistGen.current) return;
      const resolved = itemsFromBoard(saved);
      const next = mergeTimelineItems(
        resolved.length ? resolved : toSave,
        itemsRef.current,
      );
      itemsRef.current = next;
      setItems(next);
    };
    const queued = persistChain.current.then(run, run);
    persistChain.current = queued.catch(() => {});
    return queued.finally(() => {
      if (gen === persistGen.current) setSaving(false);
    });
  }, [projectId]);

  const commitItems = async (nextItems, failMessage) => {
    itemsRef.current = nextItems;
    setItems(nextItems);
    try {
      await persist(nextItems);
    } catch (err) {
      setError(err.message || failMessage);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!projectId) return;
      setLoading(true);
      setError(null);
      try {
        if (!currentProject || currentProject.id !== projectId) {
          const project = await projectsApi.get(projectId);
          if (!cancelled && project) setCurrentProject(project);
        }
        if (!cancelled) setCurrentStory?.(null);
        const board = await seriesBookBoardApi.get(projectId);
        let next = itemsFromBoard(board);
        if (next.length === 0) {
          const existing = await storiesApi.list(projectId);
          if (existing?.length) {
            next = seedBooksFromStories(existing);
            const saved = await seriesBookBoardApi.put(projectId, boardPayload(next));
            next = itemsFromBoard(saved);
          }
        }
        if (!cancelled) {
          itemsRef.current = next;
          setItems(next);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Could not load the timeline.');
          itemsRef.current = emptyBoard().items;
          setItems(emptyBoard().items);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchItem = async (cardId, fields) => {
    const next = itemsRef.current.map((item) => (
      item.id === cardId ? { ...item, ...fields } : item
    ));
    await commitItems(mergeTimelineItems(itemsRef.current, next), SAVE_TIMELINE_ITEM_ERROR);
  };

  useLayoutEffect(() => {
    const track = trackRef.current;
    const rail = railRef.current;
    if (!track) return;
    const measure = () => {
      const width = Math.max(track.scrollWidth, track.clientWidth, 800);
      const height = Math.max(track.scrollHeight, track.clientHeight, 520);
      const line = rail?.querySelector('[data-testid="timeline-line"]');
      const measured = line
        ? rail.offsetTop + line.offsetTop + Math.max(line.offsetHeight, 2) / 2
        : rail
          ? rail.offsetTop + 40
          : 248;
      // jsdom often reports 0-height layout. Keep a real wire height so
      // events stay above the line without pinning cork_y to 0.
      const lineY = measured >= 40 ? measured : 248;
      setBoardSize({ width, height, lineY });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    if (rail) observer.observe(rail);
    return () => observer.disconnect();
  }, [items, loading]);

  const patchLocal = (cardId, fields) => {
    setItems((prev) => {
      const next = prev.map((item) => (item.id === cardId ? { ...item, ...fields } : item));
      itemsRef.current = next;
      return next;
    });
  };

  const persistLayout = async () => {
    await commitItems(itemsRef.current, SAVE_TIMELINE_ITEM_ERROR);
  };

  const reportPinHeight = useCallback((cardId, height) => {
    if (!cardId || !Number.isFinite(height) || height <= 0) return;
    // jsdom often reports a track-sized box. Ignore that so a real
    // sky cork_y is not pinned to 0.
    if (height > boardSize.lineY) return;
    setPinHeights((prev) => (prev[cardId] === height ? prev : { ...prev, [cardId]: height }));
  }, [boardSize.lineY]);

  const pinCeiling = (card) => clampFreeCardTop(
    Number.POSITIVE_INFINITY,
    boardSize.lineY,
    pinHeights[card.id],
  );

  const pinTop = (card) => clampFreeCardTop(
    Number.isFinite(card.cork_y) ? card.cork_y : 0,
    boardSize.lineY,
    pinHeights[card.id],
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const current = itemsRef.current;
    const next = reorderLineItems(current, active.id, over.id);
    if (next === current) return;
    await commitItems(next, 'Could not reorder.');
  };

  const addCard = async () => {
    const current = itemsRef.current;
    const card = newIdeaCard(current);
    const at = lineInsertIndex(current, lineItems(current).length);
    await commitItems(insertItem(current, card, at), ADD_TIMELINE_ITEM_ERROR);
  };

  const addGapAt = async (lineIndex) => {
    const current = itemsRef.current;
    await commitItems(
      insertItem(current, newGapCard(), lineInsertIndex(current, lineIndex)),
      'Could not add a gap.',
    );
  };

  const addPin = async (factory, label) => {
    const current = itemsRef.current;
    const card = placeFreeCard(current, factory());
    setEditingPinId(card.id);
    await commitItems([...current, card], `Could not add an ${label}.`);
  };

  // First-click add: do not let a focused card field blur-and-save
  // disable or steal this pointer before onClick runs.
  const keepAddClick = (event) => {
    event.preventDefault();
  };

  const startBook = async (card) => {
    if (!projectId || card.type !== 'book' || card.status === 'started' || startingId) return;
    setStartingId(card.id);
    setError(null);
    try {
      const story = createStory
        ? await createStory(projectId, {
          name: card.title,
          description: card.proposition || null,
        })
        : await storiesApi.create(projectId, {
          name: card.title,
          description: card.proposition || null,
        });
      if (story?.id) {
        await storiesApi.ensureDiscoverySession(story.id);
      }
      const next = markCardStarted(itemsRef.current, card.id, story.id);
      itemsRef.current = next;
      setItems(next);
      await persist(next);
      setJustStartedId(card.id);
      await refreshStories?.();
      const project = currentProject?.id === projectId
        ? currentProject
        : await projectsApi.get(projectId);
      await openStartedBook(story, navigate, setProjectAndStory, project);
    } catch (err) {
      setError(err.message || 'Could not start this book.');
    } finally {
      setStartingId(null);
    }
  };

  const openBook = async (card) => {
    if (!card.story_id) return;
    const fromList = (stories || []).find((s) => s.id === card.story_id);
    const story = fromList || await storiesApi.get(card.story_id);
    const project = currentProject?.id === projectId
      ? currentProject
      : await projectsApi.get(projectId);
    await openStartedBook(story, navigate, setProjectAndStory, project);
  };

  const projectName = currentProject?.id === projectId ? currentProject.name : 'Series';
  const expandedBook = items.find((item) => item.id === expandedId && item.type === 'book');

  if (rawView === 'notes' && projectId) {
    return <Navigate to={projectNotesPath(projectId)} replace />;
  }

  const renderBookActions = (card) => {
    const started = card.status === 'started';
    return (
      <>
        <div className="sbb-card__actions">
          {started ? (
            <button type="button" className="sbb-card__action" onClick={() => openBook(card)}>
              Open this book
            </button>
          ) : (
            <button
              type="button"
              className="sbb-card__action"
              onClick={() => startBook(card)}
              disabled={startingId === card.id}
            >
              {startingId === card.id ? 'Starting…' : 'Start this book'}
            </button>
          )}
        </div>
        {justStartedId === card.id && (
          <p className="sbb-card__note" role="status">{START_NOTE}</p>
        )}
      </>
    );
  };

  const renderBookTitle = (card) => (
    <div className="sbb-card__top">
      <label className="sbb-card__title-label">
        <span className="sbb-visually-hidden">Book title</span>
        <textarea
          className="sbb-card__title"
          rows={1}
          value={card.title}
          onChange={(e) => {
            const title = e.target.value;
            setItems((prev) => {
              const next = prev.map((item) => (item.id === card.id ? { ...item, title } : item));
              itemsRef.current = next;
              return next;
            });
          }}
          onBlur={(e) => {
            const title = e.target.value.trim();
            if (title) patchItem(card.id, { title });
          }}
        />
      </label>
      <span className={`sbb-card__status sbb-card__status--${card.status}`}>
        {card.status === 'started' ? 'Started' : 'Idea only'}
      </span>
    </div>
  );

  const renderBookFields = (card) => (
    <>
      {renderBookTitle(card)}
      <label className="sbb-card__prop-label">
        <span className="sbb-card__prop-caption">{BOOK_LINE_LABEL}</span>
        <textarea
          className="sbb-card__proposition"
          rows={3}
          value={card.proposition}
          maxLength={400}
          onChange={(e) => {
            const proposition = clampProposition(e.target.value);
            setItems((prev) => {
              const next = prev.map((item) => (item.id === card.id ? { ...item, proposition } : item));
              itemsRef.current = next;
              return next;
            });
          }}
          onBlur={(e) => {
            const proposition = clampProposition(e.target.value).trim();
            patchItem(card.id, { proposition });
          }}
          placeholder={BOOK_LINE_PLACEHOLDER}
        />
      </label>
      <p className={`sbb-card__count${wordCount(card.proposition) > 40 ? ' is-near' : ''}`}>
        {wordCount(card.proposition)} / {PROPOSITION_WORD_CAP} words
      </p>
      {renderBookActions(card)}
    </>
  );

  const renderCompactBook = (card) => {
    const excerpt = shortLine(card.proposition);
    return (
      <>
        {renderBookTitle(card)}
        <button
          type="button"
          className="sbb-card__short"
          onClick={() => setExpandedId(card.id)}
        >
          {excerpt || BOOK_LINE_EMPTY}
        </button>
        {renderBookActions(card)}
      </>
    );
  };

  const renderGapFields = (card) => (
    <>
      <div className="sbb-card__top">
        <p className="sbb-card__gap-title">Gap</p>
      </div>
      <label className="sbb-card__prop-label">
        <span className="sbb-card__prop-caption">Length, if any</span>
        <input
          className="sbb-card__gap-input"
          value={card.length || ''}
          onChange={(e) => {
            const length = e.target.value;
            setItems((prev) => {
              const next = prev.map((item) => (item.id === card.id ? { ...item, length } : item));
              itemsRef.current = next;
              return next;
            });
          }}
          onBlur={(e) => patchItem(card.id, { length: e.target.value.trim() })}
          placeholder="20 years, a generation…"
        />
      </label>
      <label className="sbb-card__prop-label">
        <span className="sbb-card__prop-caption">Time direction</span>
        <select
          className="sbb-card__gap-select"
          value={card.direction || 'unspecified'}
          onChange={(e) => patchItem(card.id, { direction: e.target.value })}
        >
          {GAP_DIRECTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      <label className="sbb-card__prop-label">
        <span className="sbb-card__prop-caption">What happens between these books</span>
        <textarea
          className="sbb-card__proposition"
          rows={2}
          value={card.note || ''}
          onChange={(e) => {
            const note = e.target.value;
            setItems((prev) => {
              const next = prev.map((item) => (item.id === card.id ? { ...item, note } : item));
              itemsRef.current = next;
              return next;
            });
          }}
          onBlur={(e) => patchItem(card.id, { note: e.target.value.trim() })}
          placeholder="The space between these books."
        />
      </label>
    </>
  );

  const renderCompactPin = (card) => {
    const face = pinFace(card);
    return (
      <>
        <div className="sbb-card__top">
          <p className="sbb-card__gap-title">{face.title}</p>
          <span className="sbb-card__status">{face.status}</span>
        </div>
        <button
          type="button"
          className="sbb-card__short"
          onClick={() => setEditingPinId(card.id)}
        >
          <span className="sbb-card__who">{face.restWho}</span>
          {face.restHow}
        </button>
      </>
    );
  };

  const renderPinFields = (card) => {
    const face = pinFace(card);
    return (
      <>
        <div className="sbb-card__top">
          <p className="sbb-card__gap-title">{face.title}</p>
          <span className="sbb-card__status">{face.status}</span>
        </div>
        <label className="sbb-card__prop-label">
          <span className="sbb-card__prop-caption">{face.whoLabel}</span>
          <input
            className="sbb-card__gap-input"
            value={face.whoValue}
            onChange={(e) => {
              const nextValue = e.target.value;
              setItems((prev) => {
                const next = prev.map((item) => (
                  item.id === card.id ? { ...item, [face.whoField]: nextValue } : item
                ));
                itemsRef.current = next;
                return next;
              });
            }}
            onBlur={(e) => patchItem(card.id, { [face.whoField]: e.target.value.trim() })}
            placeholder={face.whoPlaceholder}
          />
        </label>
        <label className="sbb-card__prop-label">
          <span className="sbb-card__prop-caption">{face.howLabel}</span>
          <input
            className="sbb-card__gap-input"
            value={card.how || ''}
            onChange={(e) => {
              const how = clampHow(e.target.value);
              setItems((prev) => {
                const next = prev.map((item) => (item.id === card.id ? { ...item, how } : item));
                itemsRef.current = next;
                return next;
              });
            }}
            onBlur={(e) => patchItem(card.id, { how: clampHow(e.target.value).trim() })}
            placeholder={face.howPlaceholder}
          />
        </label>
        <div className="sbb-card__actions">
          <button
            type="button"
            className="sbb-card__action"
            onClick={() => setEditingPinId(null)}
          >
            Done
          </button>
        </div>
      </>
    );
  };

  const renderInsertSlot = (lineIndex, key) => SERIES_PLANNING_TOOLS && (
    <li key={key} className="sbb-timeline__insert-slot">
      <button
        type="button"
        className="sbb-timeline__insert"
        onMouseDown={keepAddClick}
        onClick={() => addGapAt(lineIndex)}
      >
        {ADD_A_GAP}
      </button>
    </li>
  );

  const addRow = !loading && (
    <div className="sbb-actions">
      {SERIES_PLANNING_TOOLS && (
        <button
          type="button"
          className="sbb-ideas"
          onMouseDown={keepAddClick}
          onClick={() => setIdeasOpen(true)}
        >
          {GET_IDEAS_LABEL}
        </button>
      )}
      <div className="sbb-add-row">
        <button type="button" className="sbb-add" onMouseDown={keepAddClick} onClick={addCard}>
          {ADD_IDEA_NOTE}
        </button>
        {SERIES_PLANNING_TOOLS && (
          <>
            <button
              type="button"
              className="sbb-add"
              onMouseDown={keepAddClick}
              onClick={() => addGapAt(lineItems(itemsRef.current).length)}
            >
              {ADD_A_GAP}
            </button>
            <button
              type="button"
              className="sbb-add"
              onMouseDown={keepAddClick}
              onClick={() => addPin(newEntranceCard, 'entrance')}
            >
              Add an entrance
            </button>
            <button
              type="button"
              className="sbb-add"
              onMouseDown={keepAddClick}
              onClick={() => addPin(newExitCard, 'exit')}
            >
              Add an exit
            </button>
            <button
              type="button"
              className="sbb-add"
              onMouseDown={keepAddClick}
              onClick={() => addPin(newRevealCard, 'reveal')}
            >
              Add a reveal
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="sbb-page sbb-page--timeline">
      <header className="sbb-page__intro">
        <p className="sbb-kicker">Series</p>
        <h1 className="sbb-page__title">{projectName}</h1>
        <nav className="sbb-switch" aria-label="Series room">
          <button
            type="button"
            className="sbb-switch__btn is-current"
            aria-current="page"
            onClick={() => navigate(`/projects/${projectId}/series`)}
          >
            Timeline
          </button>
          <button
            type="button"
            className="sbb-switch__btn"
            onClick={() => navigate(projectNotesPath(projectId))}
          >
            Docs
          </button>
        </nav>
        <p className="sbb-page__lede">
          {SERIES_PLANNING_TOOLS
            ? 'Books sit under the line. Gaps sit on it. Entrance, Exit, and Reveal sit above, tied to a spot. Moving one does not start a book.'
            : 'Your books, in order. Adding one does not start it: use Start this book when you are ready.'}
        </p>
      </header>

      {addRow}

      {error && <p className="sbb-page__error" role="alert">{error}</p>}
      {loading && <p className="sbb-page__state">Loading the timeline…</p>}

      {!loading && ideasOpen && (
        <SeriesGetIdeasPanel
          projectId={projectId}
          project={currentProject?.id === projectId ? currentProject : null}
          onClose={() => setIdeasOpen(false)}
          onProjectPatch={(next) => {
            if (next) setCurrentProject(next);
          }}
          onAcceptedPin={(card) => {
            if (!card?.id) return;
            const current = itemsRef.current;
            if (current.some((item) => item.id === card.id)) return;
            const next = [...current, card];
            itemsRef.current = next;
            setItems(next);
          }}
        />
      )}

      {!loading && expandedBook && (
        <section className="sbb-longer" role="dialog" aria-labelledby="sbb-longer-title">
          <div className="sbb-longer__bar">
            <h2 id="sbb-longer-title" className="sbb-longer__title">Longer view</h2>
            <button
              type="button"
              className="sbb-card__action"
              onClick={() => setExpandedId(null)}
            >
              Close
            </button>
          </div>
          {renderBookFields(expandedBook)}
        </section>
      )}

      {!loading && items.length === 0 && (
        <p className="sbb-page__state">{TIMELINE_EMPTY}</p>
      )}

      {!loading && (
        <div className="sbb-timeline" role="region" aria-label="Series timeline" aria-busy={saving || undefined}>
          <div className="sbb-timeline__zoom" data-testid="timeline-zoom">
            <button
              type="button"
              className="sbb-timeline__zoom-btn"
              aria-label="Zoom out"
              disabled={zoom <= ZOOM_MIN}
              onClick={() => setZoom((value) => clampZoom(value - ZOOM_STEP))}
            >
              −
            </button>
            <button
              type="button"
              className="sbb-timeline__zoom-btn"
              aria-label="Zoom in"
              disabled={zoom >= ZOOM_MAX}
              onClick={() => setZoom((value) => clampZoom(value + ZOOM_STEP))}
            >
              +
            </button>
          </div>
          <div className="sbb-timeline__scroller">
            <div
              className="sbb-timeline__track"
              ref={trackRef}
              data-zoom={zoom}
              style={{
                minHeight: Math.max(520, boardSize.height),
                zoom,
                '--line-y': `${boardSize.lineY}px`,
              }}
            >
              <svg
                className="sbb-timeline__strings"
                data-testid="timeline-strings"
                width={boardSize.width}
                height={Math.max(520, boardSize.height)}
                viewBox={`0 0 ${boardSize.width} ${Math.max(520, boardSize.height)}`}
                aria-hidden="true"
              >
                {freeItems(items).map((card) => {
                  const x1 = (Number.isFinite(card.cork_x) ? card.cork_x : 0) + FREE_CARD_WIDTH / 2;
                  const y1 = (Number.isFinite(card.cork_y) ? card.cork_y : 0) + 36;
                  const x2 = clampAttach(card.attach) * Math.max(boardSize.width, 1);
                  const y2 = boardSize.lineY;
                  return (
                    <line
                      key={`string-${card.id}`}
                      className="sbb-timeline__string"
                      data-testid={`timeline-string-${card.id}`}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                    />
                  );
                })}
              </svg>
              <div className="sbb-timeline__rail" ref={railRef} data-testid="timeline-rail">
                <div className="sbb-timeline__line" data-testid="timeline-line" />
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={lineItems(items).map((item) => item.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <ol className="sbb-timeline__row" data-testid="timeline-under" aria-label="Books and gaps">
                      {renderInsertSlot(0, 'rail-insert-0')}
                      {lineItems(items).map((card, index) => (
                        <React.Fragment key={`rail-${card.id}`}>
                          <li
                            className={`sbb-timeline__slot${
                              card.type === 'gap'
                                ? ' sbb-timeline__slot--gap'
                                : ' sbb-timeline__slot--gondola'
                            }`}
                          >
                            {card.type === 'book' && (
                              <>
                                <span
                                  className="sbb-timeline__anchor"
                                  data-testid={`timeline-anchor-${card.id}`}
                                  aria-hidden="true"
                                />
                                <span className="sbb-timeline__gondola" aria-hidden="true" />
                              </>
                            )}
                            <SortableCard card={card}>
                              {card.type === 'gap' ? renderGapFields(card) : renderCompactBook(card)}
                            </SortableCard>
                          </li>
                          {renderInsertSlot(index + 1, `rail-insert-${index + 1}`)}
                        </React.Fragment>
                      ))}
                    </ol>
                  </SortableContext>
                </DndContext>
              </div>
              <div className="sbb-timeline__free" data-testid="timeline-free" aria-label="Entrance, exit, and reveal">
                {freeItems(items).map((card) => (
                  <FreeCard
                    key={`free-${card.id}`}
                    card={card}
                    maxY={pinCeiling(card)}
                    onHeight={reportPinHeight}
                    style={{
                      left: Number.isFinite(card.cork_x) ? card.cork_x : 0,
                      top: pinTop(card),
                    }}
                    onMove={patchLocal}
                    onMoveEnd={persistLayout}
                    onOpen={editingPinId === card.id ? null : () => setEditingPinId(card.id)}
                  >
                    {editingPinId === card.id ? renderPinFields(card) : renderCompactPin(card)}
                  </FreeCard>
                ))}
              </div>
              <div className="sbb-timeline__attaches" data-testid="timeline-attaches">
                {freeItems(items).map((card) => (
                  <AttachHandle
                    key={`attach-${card.id}`}
                    card={card}
                    left={clampAttach(card.attach) * Math.max(boardSize.width, 1)}
                    top={boardSize.lineY}
                    lineWidth={boardSize.width}
                    getOriginX={() => {
                      const track = trackRef.current;
                      if (!track) return 0;
                      return track.getBoundingClientRect().left;
                    }}
                    onMove={patchLocal}
                    onMoveEnd={persistLayout}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
